import { CoreStore } from "Stores/RESTStore/CoreStore";
import { XKTSearchStore } from "./XKTSearchStore";
import { getUrlParams } from "utils/getUrlParams";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { API } from "General/API";
import { action, computed, makeObservable, observable } from "mobx";
import { BCFStore } from "./BCFStore";
import BimViewerSDK from "@pmgnet/bim-viewer-sdk";
import { messageInstance } from "App";
import i18n from "Config/i18n";
import { uniqBy } from "lodash";
import { isViewableModel } from "Components/XKTViewer/utils/resolveViewpointModel";

export interface XktModelInfo {
  fileId: number;
  fileName: string;
  position?: [number, number, number];
  scale?: [number, number, number];
}

interface InitialViewpoint {
  fileId: number;
  topicId: string;
  viewpoint: API_BcfServerViewpoint;
}

export interface InitViewerOptions {
  fileName: string;
  fileId: number;
  parentFolderId?: number;
  fullVersion?: boolean;
  isFullScreen?: boolean;
  externLinkId?: number;
  initialViewpoint?: InitialViewpoint;
}

export class XKTViewerStore extends CoreStore {
  private static readonly DISABLED_SEARCH_FILE_TYPES = [
    "xkt",
    "gltf",
    "glb",
    "laz",
    "las",
    "cityjson",
    "pcd",
    "ply",
    "stl",
  ];
  fileName: string = "";
  fileId: number = null;
  parentFolderId?: number = null;
  externLinkIdMap: Map<number, number> = new Map();
  isVisible: boolean = false;
  isXktFile: boolean = false;
  isFullVersion: boolean = false;
  searchStore: XKTSearchStore = new XKTSearchStore(this);
  bcfStore: BCFStore = new BCFStore(this);
  models: XktModelInfo[] = [];
  issueModels?: API_BcfServerIfcFileInfo[] = [];
  selectedIssueGuid?: string = "";
  sdk: BimViewerSDK;
  private initialViewpoint?: InitialViewpoint = null;

  constructor() {
    super();
    // By default TypeScript will not allow you to annotate private fields
    // https://mobx.js.org/observable-state.html#limitations
    makeObservable<XKTViewerStore, "addModelToList" | "removeModelFromList">(this, {
      // observables
      fileName: observable,
      fileId: observable,
      isVisible: observable,
      isFullVersion: observable,
      models: observable,
      selectedIssueGuid: observable,
      isSearchEnabled: computed,
      // actions
      clear: action.bound,
      hide: action.bound,
      initViewer: action.bound,
      // private actions
      addModelToList: action.bound,
      removeModelFromList: action.bound,
      setSdk: action.bound,
    });
    this.addModel = this.addModel.bind(this);
  }

  async setSdk(sdk: BimViewerSDK) {
    this.sdk = sdk;

    this.registerMessageHandlers();
    await new Promise((resolve, reject) => {
      this.sdk.on("viewerReady", async () => {
        try {
          await this.loadModels();
          resolve(undefined);
        } catch (error) {
          reject(error);
        }
      });
    });
    if (this.initialViewpoint) {
      //  when all models are loaded, show the viewpoint
      this.bcfStore.showViewpointInViewer(
        this.initialViewpoint.fileId,
        this.initialViewpoint.topicId,
        this.initialViewpoint.viewpoint
      );
      this.initialViewpoint = null; // Clear initial viewpoint after showing it
    }
  }

  showViewpointInViewer(
    modelId: number,
    topicId: string,
    viewpoint: API_BcfServerViewpoint,
    requestId?: string
  ): Promise<API_BcfServerViewpoint> {
    return this.bcfStore.showViewpointInViewer(modelId, topicId, viewpoint, requestId);
  }

  private registerMessageHandlers() {
    this.sdk.on("initBcf", () => {
      this.bcfStore.fetchProjectExtensions();
    });

    // Register for BCF issues loading
    this.sdk.on("setBcfIssues", async () => {
      this.bcfStore.init();
      const issues = await this.bcfStore.fetchAllTopics();
      if (issues) {
        this.sdk.sendMessage({
          type: "setBcfIssuesResponse",
          payload: { data: JSON.parse(JSON.stringify(issues)) },
        });
      }
    });

    // BCF viewpoints fetching
    this.sdk.on("fetchBcfViewpoints", async (payload) => {
      try {
        const viewpoints = await this.bcfStore.fetchViewpointsOfTopic(payload.topicId);
        this.sdk.sendMessage({
          type: "bcfViewpointsResponse",
          payload: { data: viewpoints, status: "success", requestId: payload.requestId },
        });
      } catch (error) {
        this.sdk.sendMessage({
          type: "bcfViewpointsResponse",
          payload: { data: [], requestId: payload.requestId, status: "error", error: error.message },
        });
      }
    });

    // BCF viewpoint snapshot fetching
    this.sdk.on("fetchBcfViewpointSnapshot", async (payload) => {
      try {
        const viewpointArb = await this.bcfStore.fetchViewpointSnapshot(payload.topicId, payload.viewpointId);
        this.sdk.sendMessage(
          {
            type: "bcfViewpointSnapshotResponse",
            payload: { data: viewpointArb, requestId: payload.requestId, status: "success" },
          },
          [viewpointArb]
        );
      } catch (error) {
        this.sdk.sendMessage({
          type: "bcfViewpointSnapshotResponse",
          payload: { data: new ArrayBuffer(0), requestId: payload.requestId, status: "error", error: error.message },
        });
      }
    });

    // Show BCF Viewpoint in Viewer
    this.sdk.on("showBcfViewpoint", async (payload) => {
      const messages = [];
      const topicId = payload.topicId;
      const requestId = payload.requestId;

      let targetModelId = +payload.modelId;

      try {
        const topicFiles = await xktViewerStore.fetchTopicFileInfo(topicId);

        if (!topicFiles || topicFiles.length === 0) {
          throw "This issue is not linked to any model";
        }

        const viewableModels = topicFiles.filter(isViewableModel);

        if (viewableModels.length === 0) {
          const modelNeedsRelink = topicFiles.find((file) => file.relinkNeeded);
          if (modelNeedsRelink) {
            throw "The linked model no longer exists";
          }
          const modelBeingProcessed = topicFiles.find(
            (file) => file.id && file.fileName && !file.relinkNeeded && !file.hasXktVersion
          );
          if (modelBeingProcessed) {
            throw "The linked model is still being processed";
          }
          throw "This issue is not linked to any model";
        }

        // Load all viewable linked models so the viewpoint renders in its original context
        await Promise.all(viewableModels.map((model) => this.loadModelIfNeeded(model.id, model.fileName)));

        // Resolve the correct model for viewpoint offset:
        // prefer the payload's modelId if viewable, otherwise use first viewable model
        const requestedModel = Number.isFinite(targetModelId)
          ? viewableModels.find((file) => file.id === targetModelId)
          : undefined;
        targetModelId = requestedModel?.id ?? viewableModels[0].id;

        messageInstance.success(i18n.t("Models in this issue have been loaded"));
      } catch (error) {
        if (typeof error === "string") {
          messages.push({ type: "error", message: error });
        } else {
          messages.push({ type: "error", message: "Failed to load model" });
        }
      }

      const uniqueMessages = uniqBy(messages, "message");
      uniqueMessages.forEach((msg) => {
        messageInstance[msg.type](i18n.t(msg.message));
      });

      await this.showViewpointInViewer(targetModelId, topicId, payload.viewpoint, requestId);
    });

    // Toggle to show deleted BCF issues
    this.sdk.on("showDeletedBcfIssues", async (payload) => {
      this.bcfStore.changeShowDeletedTopics(payload.showDeleted);
      const issues = await this.bcfStore.fetchAllTopics();
      this.sdk.sendMessage({
        type: "setBcfIssuesResponse",
        payload: { data: JSON.parse(JSON.stringify(issues)) },
      });
    });

    // Toggle to show all BCF issues (including non-linked)
    this.sdk.on("showAllBcfIssues", async (payload) => {
      this.bcfStore.showAllIssues = payload.showAllIssues;
      const issues = await this.bcfStore.fetchAllTopics();
      this.sdk.sendMessage({
        type: "setBcfIssuesResponse",
        payload: { data: JSON.parse(JSON.stringify(issues)) },
      });
    });

    // Handle model removed from viewer
    this.sdk.on("modelRemoved", (payload) => {
      const modelId = +payload.id;
      this.removeModelFromList(modelId);
      if (this.bcfStore.state !== "RAW") {
        this.bcfStore.refreshTopics();
      }
    });
  }

  async loadModels() {
    try {
      this.sdk.sendMessage({
        type: "setViewerLoading",
        payload: { loading: true },
      });
      await Promise.all(
        this.models.map(async (model) => {
          const arb = await this.fetchModel(model.fileId);
          await this.sdk.addModel(
            {
              name: model.fileName,
              id: model.fileId,
              position: model.position || [0, 0, 0],
              scale: model.scale || [1, 1, 1],
            },
            arb
          );
        })
      );
    } finally {
      this.sdk.sendMessage({
        type: "setViewerLoading",
        payload: { loading: false },
      });
    }
  }

  url(fileId: number | string): string {
    const id = typeof fileId === "string" ? Number.parseInt(fileId) : fileId;
    const externLinkId = this.getExternLinkId(id) || getUrlParams(window.location.search).linkid;
    // currently with uniqueId=0 (needed for lateron version specifiy loading)
    return externLinkId
      ? API_URL.VIEWER.getXktLinkedFile(projectsDataStore.projectId, +externLinkId, fileId)
      : API_URL.BIM.getXktFile(projectsDataStore.projectId, fileId, 0);
  }

  clear() {
    this.models = [];
    this.isVisible = false;
    this.isFullVersion = false;
    this.isXktFile = false;
    this.fileName = "";
    this.fileId = null;
    this.parentFolderId = null;
    this.externLinkIdMap = new Map();
    this.searchStore?.clear();
    this.bcfStore?.clear();
    this.selectedIssueGuid = "";
  }

  reset() {
    if (this.state === "LOADING") {
      this.cancelRequest();
    }
    this.state = "RAW";
    this.stateError = undefined;
    this.clear();
    this.abortController = new AbortController();
  }

  hide() {
    this.isVisible = false;
    // Disconnect the iframe
    this.reset();
  }

  cancelRequest() {
    this.abortController.abort();
  }

  getExternLinkId(fileId: number) {
    return this.externLinkIdMap.get(fileId);
  }

  async initViewer(
    { fileName, fileId, parentFolderId, fullVersion, externLinkId, initialViewpoint, isFullScreen }: InitViewerOptions,
    issueModels?: API_BcfServerIfcFileInfo[],
    selectedIssueGuid?: string,
    showDeletedTopics?: boolean
  ) {
    this.fileName = fileName;
    this.fileId = fileId;
    this.parentFolderId = parentFolderId;
    this.externLinkIdMap.set(fileId, externLinkId);
    this.selectedIssueGuid = selectedIssueGuid;
    this.bcfStore.changeShowDeletedTopics(showDeletedTopics);

    if (typeof fullVersion === "boolean") {
      this.isFullVersion = fullVersion;
    } else {
      this.isFullVersion = projectsDataStore.currentProject.bimViewerFullMode;
    }

    this.addModelToList({ fileId: fileId, fileName: fileName });
    if (issueModels) {
      const modelsMap = this.models.reduce((acc, model) => {
        acc[model.fileId] = model;
        return acc;
      }, {} as Record<number, XktModelInfo>);

      issueModels.forEach((item) => {
        if (!modelsMap[item.id]) {
          this.addModelToList({ fileId: item.id, fileName: item.fileName });
        }
      });
    }
    if (fileName.endsWith(".xkt")) {
      this.isXktFile = true;
    }
    if (this.isSearchEnabled) {
      this.searchStore.init({ fileId: fileId });
    }
    if (initialViewpoint) {
      this.initialViewpoint = initialViewpoint;
    }
    if (!isFullScreen) {
      this.isVisible = true;
    }
  }

  private addModelToList(props: XktModelInfo) {
    // prevent from adding same models
    if (this.models.some((model) => model.fileId === props.fileId)) {
      return;
    }

    this.models.push({
      fileId: props.fileId,
      fileName: props.fileName,
      position: props.position || [0, 0, 0],
      scale: props.scale || [1, 1, 1],
    });
  }

  private removeModelFromList(fileId: number) {
    this.models = this.models.filter((model) => model.fileId !== fileId);
  }

  async fetchModel(fileId: number): Promise<ArrayBuffer> {
    const response: File = await API.getData(this.url(fileId), null, {
      responseType: "blob",
      signal: this.abortController.signal,
    });
    return response.arrayBuffer();
  }

  async addModel(props: XktModelInfo) {
    const arb = await this.fetchModel(props.fileId);
    this.addModelToList(props);
    return this.sdk.addModel(
      { name: props.fileName, id: props.fileId, position: props.position, scale: props.scale },
      arb
    );
  }

  initBcf() {
    this.bcfStore.init();
  }

  setBcfViewpoint(modelId: number, viewpoint, requestId?: string) {
    const model = this.models.find((model) => model.fileId === modelId);
    const scaledViewpoint = JSON.parse(JSON.stringify(viewpoint));
    if (model && scaledViewpoint.perspective_camera?.camera_view_point) {
      const [xPos, zPos, yPos] = model.position;
      scaledViewpoint.perspective_camera.camera_view_point = {
        x: scaledViewpoint.perspective_camera.camera_view_point.x + xPos,
        y: scaledViewpoint.perspective_camera.camera_view_point.y + yPos,
        z: scaledViewpoint.perspective_camera.camera_view_point.z + zPos,
      };
    } else if (model && scaledViewpoint.orthogonal_camera?.camera_view_point) {
      // Handle orthogonal camera if present
      const [xPos, zPos, yPos] = model.position;
      scaledViewpoint.orthogonal_camera.camera_view_point = {
        x: scaledViewpoint.orthogonal_camera.camera_view_point.x + xPos,
        y: scaledViewpoint.orthogonal_camera.camera_view_point.y + yPos,
        z: scaledViewpoint.orthogonal_camera.camera_view_point.z + zPos,
      };
    }
    this.sdk.sendMessage({
      type: "showBcfViewpointResponse",
      payload: { data: scaledViewpoint, requestId: requestId },
    });
  }

  updateModelPosition(
    fileName: string,
    position: [number, number, number],
    scale: [number, number, number],
    fileId: number
  ) {
    const model = this.models.find((model) => model.fileId === fileId);
    if (model) {
      model.position = position;
      model.scale = scale;
      this.sdk.sendMessage({
        type: "setModelPosition",
        payload: { name: fileName, position, scale, id: fileId },
      });
    }
  }
  /**
   * Checks if a model is currently loaded in the viewer
   */
  isModelLoaded(fileId: number): boolean {
    return this.models.some((item) => item.fileId === fileId);
  }

  /**
   * Fetches file info for a topic and returns the linked models
   */
  async fetchTopicFileInfo(topicGuid: string): Promise<API_BcfServerIfcFileInfo[]> {
    try {
      const response = await API.getData<API_BcfServerIfcFileInfo[]>(
        API_URL.BCF_SERVER_EXTENSIONS.bcfServerGetTopicFileInfo(projectsDataStore.projectId, topicGuid)
      );
      return response || [];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Loads a model by file ID and file name if not already loaded.
   * Skips BCF topics refresh to preserve issue list scroll position.
   */
  async loadModelIfNeeded(fileId: number, fileName: string): Promise<void> {
    if (this.isModelLoaded(fileId)) {
      return;
    }
    // Skip fitAll and BCF refresh to preserve UI state when loading for viewpoint
    await this.addModel({ fileId, fileName });
  }

  get modelsList() {
    return Array.from(this.models.values());
  }

  get isSearchEnabled() {
    if (!this.isFullVersion || !this.fileName) {
      return false;
    }
    const ext = this.fileName.split(".").pop().toLowerCase();
    return !XKTViewerStore.DISABLED_SEARCH_FILE_TYPES.includes(ext);
  }
}

export const xktViewerStore = new XKTViewerStore();
